/**
 * Crystal-ball (水晶球 / Agora Analytics) metric definitions.
 *
 * Every constant below is taken from Agora's public definition pages, snapshot in
 * `reference/agora/pages/`:
 *  - monitor:      en/realtime-media/agora-analytics/build/monitor-and-get-alerts/monitor.md
 *  - data-insight: en/realtime-media/agora-analytics/build/explore-and-analyze-data/data-insight.md
 *  - call-search:  en/realtime-media/agora-analytics/reference/call-search-terms.md
 *
 * Alignment rules that must not drift:
 *  - video freeze: single freeze > 600 ms (native) / > 500 ms (web) counts;
 *    video freeze rate = total video freeze time / total video minutes (per stream)
 *  - audio freeze: single freeze > 200 ms counts; audio freeze rate = total audio freeze / total audio minutes
 *  - network delay rate = total end-to-end delay > 400 ms / total audio+video minutes
 *  - login success rate = joined / attempts; 5 s login success rate = joined within 5 s / attempts
 *  - real-time panel covers the last 30 minutes; heat maps refresh every 60 s, charts every 20 s
 *  - alarm thresholds: video freeze > 5%, audio freeze > 3%, network delay > 10%,
 *    join success < 95%, 5 s join success < 95%
 */
export const FREEZE_VIDEO_THRESHOLD_MS = { native: 600, web: 500 };
export const FREEZE_AUDIO_THRESHOLD_MS = 200;
export const NETWORK_DELAY_THRESHOLD_MS = 400;
export const LOGIN_SUCCESS_TARGET = 0.95;
export const LOGIN_5S_SUCCESS_TARGET = 0.95;
export const VIDEO_FREEZE_ALERT_RATE = 0.05;
export const AUDIO_FREEZE_ALERT_RATE = 0.03;
export const NETWORK_DELAY_ALERT_RATE = 0.1;
/** Real-time panel window and refresh cadence. */
export const REALTIME_WINDOW_MINUTES = 30;
export const REALTIME_BUCKET_SECONDS = 60;
export const REALTIME_CHART_REFRESH_MS = 20_000;
export const REALTIME_HEATMAP_REFRESH_MS = 60_000;
/** Success = joined within this many milliseconds. */
export const LOGIN_5S_MS = 5_000;
/** Agora ignores minutes with <=20 online users when computing per-minute call metrics. */
export const MIN_USERS_FOR_CALL_METRIC = 20;
export function videoFreezeThresholdMs(product = 'native') {
    return FREEZE_VIDEO_THRESHOLD_MS[product];
}
/**
 * Freeze counting: only individual freezes above the threshold are billable/metric input.
 * `freezes` carries raw stall durations as observed by the SDK.
 */
export function countableFreezeMs(freezes, thresholdMs) {
    let total = 0;
    for (const ms of freezes)
        if (ms > thresholdMs)
            total += ms;
    return total;
}
/** rate = Σ freeze time / Σ media duration. Returns 0 when there is no duration. */
export function freezeRate(freezeMs, durationMs) {
    if (durationMs <= 0)
        return 0;
    return Math.min(1, freezeMs / durationMs);
}
/** rate = Σ (delay time above threshold) / Σ (audio+video duration). */
export function networkDelayRate(delayedMs, durationMs) {
    if (durationMs <= 0)
        return 0;
    return Math.min(1, delayedMs / durationMs);
}
export function loginSuccessRate(successes, attempts) {
    if (attempts <= 0)
        return 0;
    return Math.min(1, successes / attempts);
}
export function computeRates(input) {
    return {
        videoFreezeRate: freezeRate(input.videoFreezeMs, input.videoDurationMs),
        audioFreezeRate: freezeRate(input.audioFreezeMs, input.audioDurationMs),
        networkDelayRate: networkDelayRate(input.networkDelayMs, input.networkDelayDurationMs),
    };
}
/**
 * Heat-map cell diagnosis. Agora colours a cell red when the metric breaches the alarm
 * threshold; we additionally expose `warn` at half the threshold for a usable gradient.
 */
export function diagnose(input) {
    const reasons = [];
    const grade = (value, threshold) => {
        if (value >= threshold)
            return 'bad';
        if (value >= threshold / 2)
            return 'warn';
        return 'good';
    };
    const loginGrade = (value) => {
        // lower is worse for success rates
        if (value < LOGIN_SUCCESS_TARGET)
            return 'bad';
        if (value < (1 + LOGIN_SUCCESS_TARGET) / 2)
            return 'warn';
        return 'good';
    };
    const video = grade(input.videoFreezeRate, VIDEO_FREEZE_ALERT_RATE);
    const audio = grade(input.audioFreezeRate, AUDIO_FREEZE_ALERT_RATE);
    const network = grade(input.networkDelayRate, NETWORK_DELAY_ALERT_RATE);
    const login = loginGrade(input.loginSuccessRate);
    const login5s = loginGrade(input.login5sSuccessRate);
    if (video === 'bad')
        reasons.push(`video freeze rate >= ${VIDEO_FREEZE_ALERT_RATE * 100}%`);
    if (audio === 'bad')
        reasons.push(`audio freeze rate >= ${AUDIO_FREEZE_ALERT_RATE * 100}%`);
    if (network === 'bad')
        reasons.push(`network delay rate >= ${NETWORK_DELAY_ALERT_RATE * 100}%`);
    if (login === 'bad')
        reasons.push(`login success rate < ${LOGIN_SUCCESS_TARGET * 100}%`);
    if (login5s === 'bad')
        reasons.push(`5s login success rate < ${LOGIN_5S_SUCCESS_TARGET * 100}%`);
    return {
        video,
        audio,
        network,
        login,
        login5s,
        alerting: reasons.length > 0,
        reasons,
    };
}
/** Dimension keys exposed by Data Insights' metric analysis. */
export const INSIGHT_DIMENSIONS = [
    'region',
    'networkType',
    'osType',
    'sdkVersion',
    'deviceType',
    'channelSize',
];
/** Granularity mapping used by `/analytics/data-insight?granularity=`. */
export const GRANULARITY_SECONDS = {
    minute: 60,
    hour: 3600,
    day: 86400,
};
export function bucketStart(epochMs, granularity) {
    const sizeMs = GRANULARITY_SECONDS[granularity] * 1000;
    return new Date(Math.floor(epochMs / sizeMs) * sizeMs).toISOString();
}
//# sourceMappingURL=metrics.js.map